import { apiLoadDoc } from '../../../../api/docs';
import { apiLoadChunks } from '../../../../api/chunks';
import { apiLoadChunksByHashes } from '../../../../api/chunks';
import { apiSearchProject } from '../../../../api/search';
import CONFIG from '../../../../config';
import { debug } from '../../../debug';
import { searchByName } from '../../../searchHelpers';
import {
  extractActiveDocContextText,
  extractRecentChatQaPairs,
  nodeTextContent
} from '../../aiChatHelpers';
import {
  summarizeSourceBreakdown,
  summarizeDocSelectionBreakdown,
  buildContextKeywordCoverage,
  analyzeImplicitTargetPrompt
} from './audit';
import {
  calculateRecencyBoost,
  rankAndBudgetContextItems
} from './ranking';
import { applyRouterGuidedAdjustments } from '../router/adjustments';

const DEFAULT_SINGLE_DOC_ACTIVE_DOC_BOOST = 1.6;
const DEFAULT_ATTACHMENT_SUFFICIENCY_MIN_ITEMS = 2;
const DEFAULT_ATTACHMENT_SUFFICIENCY_MIN_CHARS = 1200;
const TEMPLATE_FIRST_CHUNK_GUARANTEE_SCORE = 1000;
const MATCH_MARKER_START = '[[MATCH_START]]';
const MATCH_MARKER_END = '[[MATCH_END]]';
const ATTACHMENT_KEYWORD_COVERAGE_MIN = 0.5;
const AUDIT_STOPWORD_TOKENS = new Set([
  'about', 'also', 'are', 'can', 'could', 'describe', 'did', 'does', 'do', 'each', 'explain', 'from', 'give',
  'how', 'into', 'list', 'show', 'tell', 'that', 'them', 'they', 'this', 'those', 'what', 'when', 'where',
  'which', 'who', 'why', 'will', 'with', 'would', 'your'
]);
const SHORT_SIGNAL_KEYWORDS = new Set([
  'ai', 'api', 'db', 'ml', 'nlp', 'qa', 'r2', 'sql', 'ui', 'ux', 'llm'
]);

const normalizeAuditToken = (value = '') => String(value || '').trim().toLowerCase();

const isShortSignalKeyword = (value = '') => {
  const token = normalizeAuditToken(value);
  return token.length >= 2 && token.length <= 4 && SHORT_SIGNAL_KEYWORDS.has(token);
};

const isAuditKeywordToken = (value = '', { minLength = 4 } = {}) => {
  const token = normalizeAuditToken(value);
  if (!token) return false;
  if (!/[a-z]/i.test(token)) return false;
  if (AUDIT_STOPWORD_TOKENS.has(token)) return false;
  if (token.length >= minLength) return true;
  return isShortSignalKeyword(token);
};

export const normalizeContextText = (value = '') => {
  return String(value || '')
    .replace(/\s+/g, ' ')
    .trim();
};

const countOccurrences = (value = '', token = '') => {
  if (!value || !token) return 0;
  return String(value).split(token).length - 1;
};

const hasValidMatchMarkers = (value = '') => {
  const text = String(value || '');
  if (!text) return false;
  const startCount = countOccurrences(text, MATCH_MARKER_START);
  const endCount = countOccurrences(text, MATCH_MARKER_END);
  if (startCount === 0 && endCount === 0) return true;
  if (startCount !== endCount || startCount <= 0) return false;

  let cursor = 0;
  for (let index = 0; index < startCount; index += 1) {
    const startIndex = text.indexOf(MATCH_MARKER_START, cursor);
    if (startIndex < 0) return false;
    const endIndex = text.indexOf(MATCH_MARKER_END, startIndex + MATCH_MARKER_START.length);
    if (endIndex < 0) return false;
    cursor = endIndex + MATCH_MARKER_END.length;
  }

  return true;
};

const resolveSnippetWithMarkerIntegrity = ({ marked = '', plain = '' } = {}) => {
  const markedText = normalizeContextText(marked);
  const plainText = normalizeContextText(plain);
  if (!markedText) {
    return {
      text: plainText,
      usedMarkerFallback: false
    };
  }

  if (hasValidMatchMarkers(markedText)) {
    return {
      text: markedText,
      usedMarkerFallback: false
    };
  }

  if (plainText) {
    return {
      text: plainText,
      usedMarkerFallback: true
    };
  }

  const cleaned = normalizeContextText(
    markedText
      .split(MATCH_MARKER_START).join('')
      .split(MATCH_MARKER_END).join('')
  );

  return {
    text: cleaned,
    usedMarkerFallback: true
  };
};

const toFiniteNumber = (value, fallback = 0) => {
  const numeric = Number(value);
  return Number.isFinite(numeric) ? numeric : fallback;
};

const clampPositive = (value, fallback) => {
  const numeric = Number(value);
  if (!Number.isFinite(numeric) || numeric <= 0) return fallback;
  return numeric;
};

const extractDocJsonFromLoadedDoc = (row = null) => {
  if (!row || typeof row !== 'object') return null;
  const fromJson = row.content_json;
  if (fromJson && typeof fromJson === 'object' && Array.isArray(fromJson.content)) {
    return fromJson;
  }

  const raw = row.content;
  if (raw && typeof raw === 'object' && Array.isArray(raw.content)) {
    return raw;
  }

  if (typeof raw === 'string') {
    try {
      const parsed = JSON.parse(raw);
      if (parsed && typeof parsed === 'object' && Array.isArray(parsed.content)) {
        return parsed;
      }
    } catch (err) {
      return null;
    }
  }

  return null;
};

const normalizePromptComparableText = (value = '') => {
  return String(value || '')
    .toLowerCase()
    .replace(/[^a-z0-9\s]+/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();
};

const isLikelyChatSearchResult = (row = null) => {
  const docType = String(row?.doc_type || '').trim().toLowerCase();
  return docType === 'chat';
};

const tokenizeForAudit = (value = '') => {
  return Array.from(new Set(
    String(value || '')
      .toLowerCase()
      .replace(/[^a-z0-9\s]+/g, ' ')
      .split(/\s+/)
      .map((token) => token.trim())
      .filter((token) => isAuditKeywordToken(token, { minLength: 4 }))
  ));
};

const escapeRegExp = (value = '') => String(value || '').replace(/[.*+?^${}()|[\]\\]/g, '\\$&');

const countWholeWordTokenHits = (value = '', token = '') => {
  const text = String(value || '').toLowerCase();
  const normalizedToken = String(token || '').trim().toLowerCase();
  if (!text || !normalizedToken) return 0;
  const matcher = new RegExp(`\\b${escapeRegExp(normalizedToken)}\\b`, 'gi');
  const matches = text.match(matcher);
  return Array.isArray(matches) ? matches.length : 0;
};

const buildKeywordTokenItems = (value = '') => {
  return Array.from(new Set(
    String(value || '')
      .toLowerCase()
      .replace(/[^a-z0-9\s]+/g, ' ')
      .split(/\s+/)
      .map((token) => token.trim())
      .filter((token) => isAuditKeywordToken(token, { minLength: 3 }))
  )).map((name) => ({ name }));
};

const fuzzyKeywordMatch = ({ keyword = '', tokenItems = [] } = {}) => {
  const query = String(keyword || '').trim().toLowerCase();
  if (!query || !Array.isArray(tokenItems) || tokenItems.length === 0) return false;

  const minPrefixLength = query.length >= 8 ? 5 : 4;
  const prefix = query.slice(0, minPrefixLength);
  if (!prefix) return false;
  const hasPrefixCandidate = tokenItems.some((item) => {
    const token = String(item?.name || '').trim().toLowerCase();
    return token.length >= minPrefixLength && token.startsWith(prefix);
  });
  if (!hasPrefixCandidate) return false;

  const results = searchByName(tokenItems, query, {
    key: 'name',
    limit: 1,
    fuzzyThreshold: Number(CONFIG.SEARCH?.NAME_SEARCH?.FUZZY_THRESHOLD || 0.25)
  });
  return Array.isArray(results) && results.length > 0;
};

const promptReferencesValue = ({ promptComparable = '', candidate = '', minLen = 4 } = {}) => {
  const normalizedPrompt = normalizePromptComparableText(promptComparable);
  const normalizedCandidate = normalizePromptComparableText(candidate);
  if (!normalizedPrompt || !normalizedCandidate || normalizedCandidate.length < minLen) return false;
  return normalizedPrompt.includes(normalizedCandidate) || normalizedCandidate.includes(normalizedPrompt);
};

const extractFolderHintsFromDocPayload = (payload = null) => {
  if (!payload || typeof payload !== 'object') return [];
  const candidates = [
    payload.folder_name,
    payload.folderName,
    payload.folder_path,
    payload.folderPath,
    payload.path,
    payload.full_path,
    payload.fullPath,
    payload.parent_path,
    payload.parentPath
  ];
  return Array.from(new Set(
    candidates
      .map((value) => normalizeContextText(value || ''))
      .filter(Boolean)
  ));
};

const findHeadingPromptMatch = ({ headingSignals = [], promptComparable = '' } = {}) => {
  const normalizedPrompt = normalizePromptComparableText(promptComparable);
  if (!normalizedPrompt || !Array.isArray(headingSignals) || headingSignals.length === 0) {
    return null;
  }

  for (const heading of headingSignals) {
    const headingText = normalizeContextText(heading?.text || '');
    const headingComparable = normalizePromptComparableText(headingText);
    if (!headingComparable || headingComparable.length < 4) continue;
    if (promptReferencesValue({ promptComparable: normalizedPrompt, candidate: headingComparable, minLen: 4 })) {
      return {
        headingText,
        headingComparable,
        headingLevel: Math.max(1, Math.min(6, Number(heading?.level || 6)))
      };
    }
  }

  return null;
};

const extractSectionScopeTarget = (value = '') => {
  const text = String(value || '');
  if (!text) return null;

  const sectionMatch = text.match(/\b(?:under|in)\s+(?:the\s+)?([a-z0-9][a-z0-9\s&/_-]{2,80}?)\s+section\b/i);
  const rawCandidate = normalizeContextText(sectionMatch?.[1] || '');
  if (!rawCandidate) return null;

  const normalized = normalizeContextText(
    rawCandidate
      .toLowerCase()
      .replace(/\b(the|my|our)\b/g, ' ')
  );

  if (!normalized || normalized.length < 3) return null;
  return {
    raw: rawCandidate,
    normalized
  };
};

const collectTextLeafNodes = (node = null, leaves = []) => {
  if (!node || typeof node !== 'object') return leaves;
  if (node.type === 'text') {
    leaves.push(node);
    return leaves;
  }
  const content = Array.isArray(node?.content) ? node.content : [];
  content.forEach((child) => collectTextLeafNodes(child, leaves));
  return leaves;
};

const textNodeHasBoldMark = (node = null) => {
  const marks = Array.isArray(node?.marks) ? node.marks : [];
  return marks.some((mark) => {
    const type = String(mark?.type || '').trim().toLowerCase();
    return type === 'bold' || type === 'strong';
  });
};

const isPseudoHeaderParagraphNode = (node = null) => {
  if (!node || typeof node !== 'object') return false;
  if (String(node?.type || '').trim().toLowerCase() !== 'paragraph') return false;

  const text = normalizeContextText(nodeTextContent(node));
  if (!text) return false;
  if (text.length < 4 || text.length > 120) return false;

  const wordCount = text.split(/\s+/).filter(Boolean).length;
  if (wordCount < 1 || wordCount > 14) return false;
  if (/[.!?]$/.test(text)) return false;

  const textLeaves = collectTextLeafNodes(node, [])
    .filter((leaf) => String(leaf?.text || '').trim().length > 0);
  if (textLeaves.length === 0) return false;

  return textLeaves.every((leaf) => textNodeHasBoldMark(leaf));
};

const collectHeadingSignals = (node = null, headings = []) => {
  if (!node || typeof node !== 'object') return headings;

  const type = String(node?.type || '').trim().toLowerCase();
  if (type === 'heading' || isPseudoHeaderParagraphNode(node)) {
    const headingText = normalizeContextText(nodeTextContent(node));
    const level = type === 'heading'
      ? (() => {
          const rawLevel = Number(node?.attrs?.level || 0);
          return Number.isFinite(rawLevel) && rawLevel > 0
            ? Math.max(1, Math.min(6, Math.round(rawLevel)))
            : 6;
        })()
      : 3;
    if (headingText) {
      headings.push({
        text: headingText.toLowerCase(),
        level
      });
    }
  }

  const content = Array.isArray(node?.content) ? node.content : [];
  content.forEach((child) => collectHeadingSignals(child, headings));
  return headings;
};

const extractChunkHeadingSignals = (chunkJson = null) => {
  return collectHeadingSignals(chunkJson, []);
};

const extractKeywordAnchoredSnippets = ({ text = '', userText = '', maxSnippets = 3, windowChars = 320 } = {}) => {
  const source = normalizeContextText(text);
  if (!source) return [];
  const lowered = source.toLowerCase();
  const keywords = tokenizeForAudit(userText).slice(0, 10);
  const snippets = [];
  const seen = new Set();

  for (const keyword of keywords) {
    if (snippets.length >= maxSnippets) break;
    const needle = String(keyword || '').trim().toLowerCase();
    if (!needle) continue;
    const matchIndex = lowered.indexOf(needle);
    if (matchIndex < 0) continue;

    const half = Math.max(80, Math.floor(windowChars / 2));
    const start = Math.max(0, matchIndex - half);
    const end = Math.min(source.length, start + windowChars);
    const snippet = normalizeContextText(source.slice(start, end));
    if (!snippet) continue;
    const key = snippet.toLowerCase();
    if (seen.has(key)) continue;
    seen.add(key);
    snippets.push({ keyword: needle, snippet });
  }

  return snippets;
};

const evaluateAttachmentSufficiency = ({
  items = [],
  includeActiveDoc = true,
  minItems = DEFAULT_ATTACHMENT_SUFFICIENCY_MIN_ITEMS,
  minChars = DEFAULT_ATTACHMENT_SUFFICIENCY_MIN_CHARS
} = {}) => {
  const attachmentItems = (Array.isArray(items) ? items : []).filter((item) => {
    const source = String(item?.source || '').trim();
    if (source === 'linkedDocs') return true;
    return includeActiveDoc && source === 'activeDoc';
  });
  const attachmentChars = attachmentItems.reduce((total, item) => {
    return total + String(item?.text || '').trim().length;
  }, 0);
  const attachmentCount = attachmentItems.length;
  const requiredItems = clampPositive(minItems, DEFAULT_ATTACHMENT_SUFFICIENCY_MIN_ITEMS);
  const requiredChars = clampPositive(minChars, DEFAULT_ATTACHMENT_SUFFICIENCY_MIN_CHARS);

  const isSufficient = attachmentCount >= requiredItems
    || attachmentChars >= requiredChars;

  return {
    attachmentCount,
    attachmentChars,
    isSufficient,
    requiredItems,
    requiredChars
  };
};

export const buildContextRetrievalPipeline = async ({
  docJson,
  userText,
  queryPlan,
  contextPlan,
  routerContract = null,
  signal,
  searchProject = apiSearchProject,
  loadLinkedDocContent = apiLoadDoc,
  loadLinkedDocChunks = apiLoadChunks
} = {}) => {
  const plan = contextPlan && typeof contextPlan === 'object' ? contextPlan : {};
  const activeDocId = Number(plan.activeDocId || 0);
  const activeDocType = String(plan.activeDocType || '').trim().toLowerCase();
  const isActiveChatDoc = activeDocType === 'chat';
  const exhaustiveScopeRequested = Boolean(queryPlan?.exhaustiveRequested);
  const budget = queryPlan?.retrievalBudget || {};
  const projectId = Number(plan.projectId || 0);
  const maxContextItems = clampPositive(budget.maxContextItems, 14);
  const maxContextChars = clampPositive(budget.maxContextChars, 12000);
  const maxProjectResults = clampPositive(budget.maxProjectResults, 24);
  const maxLinkedDocs = clampPositive(budget.maxLinkedDocs, 6);
  const recentQaPairs = clampPositive(budget.historyRecentQaPairs, 3);
  const adjacentChunkExpansionEnabled = CONFIG.AI?.CHAT_ORCHESTRATION?.ADJACENT_CHUNK_EXPANSION_ENABLED !== false;
  const adjacentChunkMaxPerDoc = clampPositive(
    CONFIG.AI?.CHAT_ORCHESTRATION?.ADJACENT_CHUNK_MAX_PER_DOC,
    3
  );
  const adjacentChunkScoreDecay = Math.max(0, Number(CONFIG.AI?.CHAT_ORCHESTRATION?.ADJACENT_CHUNK_SCORE_DECAY || 0.08));
  const adjacentChunkMinSourceScore = Math.max(0, Number(CONFIG.AI?.CHAT_ORCHESTRATION?.ADJACENT_CHUNK_MIN_SOURCE_SCORE || 1.1));
  const adjacentChunkContinueMinScore = Math.max(0, Number(CONFIG.AI?.CHAT_ORCHESTRATION?.ADJACENT_CHUNK_CONTINUE_MIN_SCORE || 0.8));
  const projectChatNoiseEnabled = CONFIG.AI?.CHAT_ORCHESTRATION?.PROJECT_CHAT_NOISE_ENABLED !== false;
  const projectSearchExcludeChatDocsWhenActiveChat = CONFIG.AI?.CHAT_ORCHESTRATION?.PROJECT_SEARCH_EXCLUDE_CHAT_DOCS_WHEN_ACTIVE_CHAT !== false;
  const projectChatNoiseDeprioritizeMultiplier = Math.max(
    0.05,
    Math.min(1, Number(CONFIG.AI?.CHAT_ORCHESTRATION?.PROJECT_CHAT_NOISE_DEPRIORITIZE_MULTIPLIER || 0.45))
  );
  const projectChatNoisePerDocMaxChunks = clampPositive(
    CONFIG.AI?.CHAT_ORCHESTRATION?.PROJECT_CHAT_NOISE_PER_DOC_MAX_CHUNKS,
    1
  );
  const projectChatFallbackMultiplier = Math.max(
    0.05,
    Math.min(1, Number(CONFIG.AI?.CHAT_ORCHESTRATION?.PROJECT_CHAT_FALLBACK_MULTIPLIER || 0.2))
  );
  const projectNonChatPreferenceMultiplier = Math.max(
    1,
    Number(CONFIG.AI?.CHAT_ORCHESTRATION?.PROJECT_NON_CHAT_PREFERENCE_MULTIPLIER || 1.9)
  );
  const projectChatMaxChunks = clampPositive(
    CONFIG.AI?.CHAT_ORCHESTRATION?.PROJECT_CHAT_MAX_CHUNKS,
    8
  );
  const projectChatNoiseSearchLimitMultiplier = Math.max(
    1,
    Number(CONFIG.AI?.CHAT_ORCHESTRATION?.PROJECT_CHAT_NOISE_SEARCH_LIMIT_MULTIPLIER || 8)
  );
  const projectChatNoiseSearchLimitCap = clampPositive(
    CONFIG.AI?.CHAT_ORCHESTRATION?.PROJECT_CHAT_NOISE_SEARCH_LIMIT_CAP,
    240
  );
  const projectSearchDeepLimitCap = clampPositive(
    CONFIG.AI?.CHAT_ORCHESTRATION?.PROJECT_SEARCH_DEEP_LIMIT_CAP,
    1200
  );
  const projectSearchMinNonChatRows = clampPositive(
    CONFIG.AI?.CHAT_ORCHESTRATION?.PROJECT_SEARCH_MIN_NON_CHAT_ROWS,
    6
  );
  const projectSearchDocDiversityEnabled = CONFIG.AI?.CHAT_ORCHESTRATION?.PROJECT_SEARCH_DOC_DIVERSITY_ENABLED !== false;
  const projectSearchPerDocMaxChunks = clampPositive(
    CONFIG.AI?.CHAT_ORCHESTRATION?.PROJECT_SEARCH_PER_DOC_MAX_CHUNKS,
    2
  );
  const projectSearchPerDocScoreDecay = Math.max(
    0,
    Number(CONFIG.AI?.CHAT_ORCHESTRATION?.PROJECT_SEARCH_PER_DOC_SCORE_DECAY || 0.14)
  );
  const shouldApplyProjectDocDiversity = projectSearchDocDiversityEnabled
    && (isActiveChatDoc || exhaustiveScopeRequested);
  const activeChatTailMaxChunks = clampPositive(
    CONFIG.AI?.CHAT_ORCHESTRATION?.ACTIVE_CHAT_TAIL_MAX_CHUNKS,
    3
  );
  const summaryMinCandidateScore = Math.max(
    0,
    Number(CONFIG.AI?.CHAT_ORCHESTRATION?.SUMMARY_REFRESH_MIN_CANDIDATE_SCORE || 0.5)
  );
  const summaryMaxBackfillPerPrompt = clampPositive(
    CONFIG.AI?.CHAT_ORCHESTRATION?.SUMMARY_REFRESH_BACKFILL_CAP_PER_PROMPT,
    16
  );
  const summaryMinChunkChars = Math.max(
    0,
    Number(CONFIG.AI?.CHAT_ORCHESTRATION?.SUMMARY_MIN_CHUNK_CHARS || 180)
  );
  const fullDocEscalationEnabled = CONFIG.AI?.CHAT_ORCHESTRATION?.FULL_DOC_ESCALATION_ENABLED !== false;
  const fullDocEscalationMaxDocs = clampPositive(
    CONFIG.AI?.CHAT_ORCHESTRATION?.FULL_DOC_ESCALATION_MAX_DOCS,
    1
  );
  const fullDocEscalationMinCoverageRatio = Math.max(
    0,
    Math.min(1, Number(CONFIG.AI?.CHAT_ORCHESTRATION?.FULL_DOC_ESCALATION_MIN_COVERAGE_RATIO || 0.45))
  );
  const fullDocEscalationMaxCharsPerDoc = clampPositive(
    CONFIG.AI?.CHAT_ORCHESTRATION?.FULL_DOC_ESCALATION_MAX_CHARS_PER_DOC,
    1800
  );
  const activeDocScoreBoost = clampPositive(
    plan.singleDocActiveDocBoost,
    DEFAULT_SINGLE_DOC_ACTIVE_DOC_BOOST
  );
  const recencyWeight = toFiniteNumber(plan.recencyWeight, 0.35);
  const recencyDecayTurns = clampPositive(plan.recencyDecayTurns, 8);
  const sourcePriorities = Array.isArray(queryPlan?.sourcePriorities) ? queryPlan.sourcePriorities : [];
  const linkedDocIds = Array.from(new Set(
    (Array.isArray(plan.linkedDocIds) ? plan.linkedDocIds : [])
      .map(value => Number(value))
      .filter(value => Number.isFinite(value) && value > 0)
  ))
    .filter(value => value !== activeDocId)
    .slice(0, maxLinkedDocs);
  const templateDocId = Number(plan.templateDocId || 0);
  const hasTemplateDoc = Number.isFinite(templateDocId) && templateDocId > 0;
  const sectionScope = extractSectionScopeTarget(userText);
  const promptComparable = normalizePromptComparableText(userText);
  const promptTargetSignals = analyzeImplicitTargetPrompt({ userText });
  const intentContract = queryPlan?.intentContract && typeof queryPlan.intentContract === 'object'
    ? queryPlan.intentContract
    : null;
  const intentContractEnabled = Boolean(intentContract && String(intentContract.source || '').trim().toLowerCase() === 'model');
  const intentContractDocHints = intentContractEnabled && Array.isArray(intentContract?.docHints)
    ? intentContract.docHints
      .map((entry) => {
        const label = normalizeContextText(entry?.label || '');
        return {
          label,
          normalized: normalizePromptComparableText(label),
          confidence: Number.isFinite(Number(entry?.confidence))
            ? Math.max(0, Math.min(1, Number(entry.confidence)))
            : null
        };
      })
      .filter((entry) => Boolean(entry.normalized))
    : [];
  const intentContractPrimaryKeywords = intentContractEnabled && Array.isArray(intentContract?.primaryKeywords)
    ? tokenizeForAudit(intentContract.primaryKeywords.join(' ')).slice(0, 8)
    : [];
  const intentContractDocHintMultiplier = intentContractEnabled
    ? Math.max(1, Number(CONFIG.AI?.CHAT_ORCHESTRATION?.INTENT_CONTRACT_DOC_HINT_MULTIPLIER || 1.35))
    : 1;
  const intentContractFolderHintMultiplier = intentContractEnabled
    ? Math.max(1, Number(CONFIG.AI?.CHAT_ORCHESTRATION?.INTENT_CONTRACT_FOLDER_HINT_MULTIPLIER || 1.2))
    : 1;
  const hotSignalsEnabled = CONFIG.AI?.CHAT_ORCHESTRATION?.HOT_HIT_SIGNALS_ENABLED !== false;
  const hotDocNameMultiplier = hotSignalsEnabled
    ? Math.max(1, Number(CONFIG.AI?.CHAT_ORCHESTRATION?.HOT_HIT_DOC_NAME_MULTIPLIER || 3))
    : 1;
  const hotFolderNameMultiplier = hotSignalsEnabled
    ? Math.max(1, Number(CONFIG.AI?.CHAT_ORCHESTRATION?.HOT_HIT_FOLDER_NAME_MULTIPLIER || 2))
    : 1;
  const hotHeaderMultiplier = hotSignalsEnabled
    ? Math.max(1, Number(CONFIG.AI?.CHAT_ORCHESTRATION?.HOT_HIT_HEADER_MULTIPLIER || 5))
    : 1;
  const hotContentKeywordScore = hotSignalsEnabled
    ? Math.max(0, Number(CONFIG.AI?.CHAT_ORCHESTRATION?.HOT_HIT_CONTENT_KEYWORD_SCORE || 0.2))
    : 0;
  const hotContextDocMultiplier = hotSignalsEnabled
    ? Math.max(1, Number(CONFIG.AI?.CHAT_ORCHESTRATION?.HOT_HIT_CONTEXT_DOC_MULTIPLIER || 1.5))
    : 1;
  const hotHeaderTrailingChunks = hotSignalsEnabled
    ? Math.max(1, Number(CONFIG.AI?.CHAT_ORCHESTRATION?.HOT_HIT_HEADER_TRAILING_CHUNKS || 4))
    : 0;
  const hotHeaderStopAtBoundary = hotSignalsEnabled
    ? (CONFIG.AI?.CHAT_ORCHESTRATION?.HOT_HIT_HEADER_STOP_AT_BOUNDARY !== false)
    : true;
  const isSingleDocContext = activeDocId > 0 && linkedDocIds.length === 0;
  let excludedActiveChatDocSearchHits = 0;
  let plannedProjectQueries = [];
  const attemptedProjectQueries = [];
  const projectSearchErrors = [];
  const fetchedChunkIds = new Set();
  let projectChatNoiseSuppressedRows = 0;
  let projectChatNoiseDownscoredRows = 0;
  let projectChatNoiseCappedRows = 0;
  let projectChatFallbackRows = 0;
  let projectDocDiversityCappedRows = 0;
  let projectDocDiversityDownscoredRows = 0;
  const projectDocCandidateCounts = new Map();
  const fullDocEscalationErrors = [];
  const fullDocEscalationDocIds = [];
  let fullDocEscalationCoverageBefore = null;
  let fullDocEscalationCoverageAfter = null;
  let fullDocEscalationTriggered = false;
  const projectDocSignals = new Map();
  const useStrictSingleDoc = Boolean(plan.singleDocStrictActiveDoc && isSingleDocContext);
  const retrievalReasons = [];
  const addRetrievalReason = (reason) => {
    const value = String(reason || '').trim();
    if (!value) return;
    if (!retrievalReasons.includes(value)) {
      retrievalReasons.push(value);
    }
  };
  const effectiveSourcePriorities = useStrictSingleDoc
    ? [
      { source: 'activeDoc', priority: 1 },
      ...sourcePriorities.filter((entry) => String(entry?.source || '').trim() !== 'activeDoc')
        .map((entry, index) => ({
          source: String(entry?.source || '').trim(),
          priority: index + 2
        }))
    ]
    : sourcePriorities;

  if (queryPlan?.disambiguation?.useSingleDocDisambiguation) {
    addRetrievalReason('single-doc-context-boost');
  }
  if (useStrictSingleDoc) {
    addRetrievalReason('single-doc-strict-active-doc-priority');
  }
  if (sectionScope) {
    addRetrievalReason('section_scope_detected');
  }

  const candidates = [];

  if (plan.includeActiveDoc && !isActiveChatDoc) {
    const activeText = extractActiveDocContextText({
      docJson,
      maxChars: Math.min(maxContextChars, 5000)
    });
    if (activeText) {
      candidates.push({
        source: 'activeDoc',
        text: activeText,
        baseScore: isSingleDocContext ? 1.4 * activeDocScoreBoost : 1.4,
        recencyBoost: 0,
        meta: {
          activeDocId: plan.activeDocId || null,
          trigger: 'active_doc_context'
        }
      });
    }
  }

  if (plan.includeChatHistory && !isActiveChatDoc) {
    const recentPairs = extractRecentChatQaPairs({ docJson, lastQaPairs: recentQaPairs });
    const reversed = recentPairs.slice().reverse();
    reversed.forEach((pair, index) => {
      const userPart = normalizeContextText(pair?.user?.content || '');
      const assistantPart = normalizeContextText(pair?.assistant?.content || '');
      const text = [
        userPart ? `User: ${userPart}` : '',
        assistantPart ? `Assistant: ${assistantPart}` : ''
      ].filter(Boolean).join('\n');
      if (!text) return;

      candidates.push({
        source: 'chatHistory',
        text,
        // Chat history is fallback context and should not outrank committed doc evidence.
        baseScore: 0.32,
        recencyRank: index,
        recencyBoost: calculateRecencyBoost({ rank: index, recencyWeight, recencyDecayTurns }),
        meta: {
          pairIndexFromLatest: index,
          trigger: 'chat_history_recent_qa'
        }
      });
    });
  }

  if (plan.includeLinkedDocs && linkedDocIds.length > 0 && typeof loadLinkedDocContent === 'function') {
    const linkedResults = await Promise.allSettled(
      linkedDocIds.map(async (linkedDocId) => {
        const payload = await loadLinkedDocContent(linkedDocId);
        const chunks = typeof loadLinkedDocChunks === 'function'
          ? await loadLinkedDocChunks(linkedDocId)
          : [];
        return {
          linkedDocId,
          payload,
          chunks
        };
      })
    );

    for (const row of linkedResults) {
      if (row.status !== 'fulfilled') continue;
      const linkedDocId = Number(row.value?.linkedDocId || 0);
      if (!Number.isFinite(linkedDocId) || linkedDocId <= 0) continue;

      const payload = row.value?.payload || null;
      const chunkRows = Array.isArray(row.value?.chunks) ? row.value.chunks : [];
      const docJsonValue = extractDocJsonFromLoadedDoc(payload);
      const fallbackText = normalizeContextText(payload?.content || '');
      const linkedText = docJsonValue
        ? extractActiveDocContextText({
          docJson: docJsonValue,
          maxChars: Math.min(maxContextChars, 3200)
        })
        : fallbackText;
      const linkedLongText = docJsonValue
        ? extractActiveDocContextText({
          docJson: docJsonValue,
          maxChars: Math.max(Math.min(maxContextChars * 2, 24000), 6000)
        })
        : fallbackText;
      if (!linkedText && chunkRows.length === 0) continue;

      const docName = String(payload?.name || '').trim();
      const docNameHot = hotSignalsEnabled && promptReferencesValue({ promptComparable, candidate: docName, minLen: 4 });
      const contractDocHintMatch = intentContractDocHints.some((hint) => {
        if (!hint?.normalized || hint.normalized.length < 3) return false;
        const normalizedDocName = normalizePromptComparableText(docName);
        return Boolean(normalizedDocName && (normalizedDocName.includes(hint.normalized) || hint.normalized.includes(normalizedDocName)));
      });
      const folderHints = extractFolderHintsFromDocPayload(payload);
      const folderHot = hotSignalsEnabled
        && folderHints.some((value) => promptReferencesValue({ promptComparable, candidate: value, minLen: 3 }));
      const contractFolderHintMatch = intentContractDocHints.some((hint) => {
        if (!hint?.normalized || hint.normalized.length < 3) return false;
        return folderHints.some((folderValue) => {
          const normalizedFolder = normalizePromptComparableText(folderValue);
          return Boolean(normalizedFolder && (normalizedFolder.includes(hint.normalized) || hint.normalized.includes(normalizedFolder)));
        });
      });
      const contextDocHotMultiplier = hotContextDocMultiplier;
      let linkedDocScoreMultiplier = contextDocHotMultiplier;
      if (docNameHot) {
        linkedDocScoreMultiplier *= hotDocNameMultiplier;
        addRetrievalReason('hot_doc_name_match');
      }
      if (contractDocHintMatch) {
        linkedDocScoreMultiplier *= intentContractDocHintMultiplier;
        addRetrievalReason('intent_contract_doc_hint_match');
      }
      if (folderHot) {
        linkedDocScoreMultiplier *= hotFolderNameMultiplier;
        addRetrievalReason('hot_folder_name_match');
      }
      if (contractFolderHintMatch) {
        linkedDocScoreMultiplier *= intentContractFolderHintMultiplier;
        addRetrievalReason('intent_contract_folder_hint_match');
      }
      const isTemplateDoc = hasTemplateDoc && linkedDocId === templateDocId;

      if (chunkRows.length > 0) {
        const chunkCandidates = [];
        const headingSignalsByIndex = chunkRows.map((chunkRow) => {
          return extractChunkHeadingSignals(chunkRow?.content_json || {});
        });
        const keywordMatchedIndexes = new Set();
        const headerExpansionSeeds = [];
        const userKeywords = tokenizeForAudit(userText).slice(0, 12);
        for (let chunkIndex = 0; chunkIndex < chunkRows.length; chunkIndex += 1) {
          const chunkRow = chunkRows[chunkIndex];
          const chunkHash = String(chunkRow?.chunk_hash || '').trim();
          if (chunkHash) fetchedChunkIds.add(chunkHash);
          const chunkText = normalizeContextText(nodeTextContent(chunkRow?.content_json || {}));
          if (!chunkText) continue;

          let keywordMatches = 0;
          const textLower = chunkText.toLowerCase();
          const chunkTokenItems = buildKeywordTokenItems(chunkText);
          const headingSignals = Array.isArray(headingSignalsByIndex[chunkIndex])
            ? headingSignalsByIndex[chunkIndex]
            : [];
          const headingPromptMatch = hotSignalsEnabled ? findHeadingPromptMatch({
            headingSignals,
            promptComparable
          }) : null;
          const sectionHeadingSignals = sectionScope
            ? headingSignals.filter((heading) => String(heading?.text || '').includes(sectionScope.normalized))
            : [];
          const sectionHeadingMatch = sectionHeadingSignals.length > 0;
          const hotHeaderMatch = Boolean(headingPromptMatch);
          const sectionMentionMatch = Boolean(sectionScope && !sectionHeadingMatch && textLower.includes(sectionScope.normalized));

          for (const token of userKeywords) {
            const normalizedToken = String(token || '').toLowerCase();
            if (textLower.includes(normalizedToken) || fuzzyKeywordMatch({ keyword: normalizedToken, tokenItems: chunkTokenItems })) {
              keywordMatches += 1;
            }
          }

          if (keywordMatches > 0) {
            keywordMatchedIndexes.add(chunkIndex);
          }

          if (sectionHeadingMatch) {
            const strongestLevel = sectionHeadingSignals.reduce((minLevel, heading) => {
              const nextLevel = Math.max(1, Math.min(6, Number(heading?.level || 6)));
              return Math.min(minLevel, nextLevel);
            }, 6);
            headerExpansionSeeds.push({
              chunkIndex,
              headingLevel: strongestLevel,
              reason: 'section_heading'
            });
          }

          if (hotHeaderMatch) {
            headerExpansionSeeds.push({
              chunkIndex,
              headingLevel: Math.max(1, Math.min(6, Number(headingPromptMatch?.headingLevel || 6))),
              reason: 'prompt_heading'
            });
            addRetrievalReason('hot_header_match');
          }

          const contentScore = keywordMatches * hotContentKeywordScore;
          const hotHeaderScoreMultiplier = hotHeaderMatch || sectionHeadingMatch
            ? hotHeaderMultiplier
            : 1;
          const sectionMentionMultiplier = sectionMentionMatch ? 1.2 : 1;
          const scoreMultiplier = linkedDocScoreMultiplier * hotHeaderScoreMultiplier * sectionMentionMultiplier;
          const baseScore = ((isTemplateDoc ? 1.04 : 0.96) + contentScore) * scoreMultiplier;

          chunkCandidates.push({
            source: 'linkedDocs',
            text: docName ? `${docName}: ${chunkText}` : chunkText,
            baseScore,
            recencyBoost: 0,
            meta: {
              docId: linkedDocId,
              docName: docName || null,
              label: docName || `Doc ${linkedDocId}`,
              contextRole: isTemplateDoc ? 'style_reference' : 'supporting',
              trigger: sectionHeadingMatch
                ? 'linked_doc_section_heading_chunk'
                : (hotHeaderMatch
                  ? 'linked_doc_header_heading_chunk'
                  : (keywordMatches > 0 ? 'linked_doc_keyword_chunk' : (sectionMentionMatch ? 'linked_doc_section_chunk' : 'linked_doc_chunk_hit'))),
              chunkHash,
              chunkIndex,
              sectionScope: sectionScope?.normalized || null,
              headerMatched: hotHeaderMatch,
              docNameMatched: docNameHot,
              folderMatched: folderHot,
              scoreMultiplier: Number(scoreMultiplier.toFixed(3)),
              matchedKeywordCount: keywordMatches
            }
          });
        }

        if (keywordMatchedIndexes.size > 0) {
          const neighborIndexes = new Set();
          keywordMatchedIndexes.forEach((index) => {
            neighborIndexes.add(index - 1);
            neighborIndexes.add(index + 1);
          });

          for (const neighborIndex of neighborIndexes) {
            if (!Number.isFinite(neighborIndex) || neighborIndex < 0 || neighborIndex >= chunkRows.length) continue;
            if (keywordMatchedIndexes.has(neighborIndex)) continue;
            const chunkRow = chunkRows[neighborIndex];
            const chunkHash = String(chunkRow?.chunk_hash || '').trim();
            if (chunkHash) fetchedChunkIds.add(chunkHash);
            const chunkText = normalizeContextText(nodeTextContent(chunkRow?.content_json || {}));
            if (!chunkText) continue;

            chunkCandidates.push({
              source: 'linkedDocs',
              text: docName ? `${docName}: ${chunkText}` : chunkText,
              baseScore: isTemplateDoc ? 0.98 : 0.9,
              recencyBoost: 0,
              meta: {
                docId: linkedDocId,
                docName: docName || null,
                label: docName || `Doc ${linkedDocId}`,
                contextRole: isTemplateDoc ? 'style_reference' : 'supporting',
                trigger: 'linked_doc_neighbor_chunk',
                chunkHash,
                chunkIndex: neighborIndex
              }
            });
          }
        }

        if (hotSignalsEnabled && headerExpansionSeeds.length > 0) {
          const trailingAdded = new Set();
          const maxTrailingChunksPerSeed = Math.max(1, hotHeaderTrailingChunks);
          for (const seed of headerExpansionSeeds.sort((left, right) => left.chunkIndex - right.chunkIndex)) {
            let addedForSeed = 0;
            const seedLevel = Math.max(1, Math.min(6, Number(seed?.headingLevel || 6)));

            for (let trailingIndex = seed.chunkIndex + 1; trailingIndex < chunkRows.length; trailingIndex += 1) {
              const headingSignals = Array.isArray(headingSignalsByIndex[trailingIndex])
                ? headingSignalsByIndex[trailingIndex]
                : [];
              const boundaryFound = headingSignals.some((heading) => {
                const headingLevel = Math.max(1, Math.min(6, Number(heading?.level || 6)));
                const headingText = String(heading?.text || '');
                const sectionText = String(sectionScope?.normalized || '').trim();
                return headingLevel <= seedLevel && (!sectionText || !headingText.includes(sectionText));
              });
              if (hotHeaderStopAtBoundary && boundaryFound) break;
              if (addedForSeed >= maxTrailingChunksPerSeed) break;
              if (trailingAdded.has(trailingIndex)) continue;

              const chunkRow = chunkRows[trailingIndex];
              const chunkHash = String(chunkRow?.chunk_hash || '').trim();
              if (chunkHash) fetchedChunkIds.add(chunkHash);
              const chunkText = normalizeContextText(nodeTextContent(chunkRow?.content_json || {}));
              if (!chunkText) continue;

              trailingAdded.add(trailingIndex);
              addedForSeed += 1;

              const distance = Math.max(1, trailingIndex - seed.chunkIndex);
              const trailingMultiplier = linkedDocScoreMultiplier * (seed?.reason === 'section_heading' ? hotHeaderMultiplier : 1);
              chunkCandidates.push({
                source: 'linkedDocs',
                text: docName ? `${docName}: ${chunkText}` : chunkText,
                baseScore: ((isTemplateDoc ? 1.08 : 1.0) - ((distance - 1) * 0.04)) * trailingMultiplier,
                recencyBoost: 0,
                meta: {
                  docId: linkedDocId,
                  docName: docName || null,
                  label: docName || `Doc ${linkedDocId}`,
                  contextRole: isTemplateDoc ? 'style_reference' : 'supporting',
                  trigger: seed?.reason === 'section_heading'
                    ? 'linked_doc_section_trailing_chunk'
                    : 'linked_doc_header_trailing_chunk',
                  chunkHash,
                  chunkIndex: trailingIndex,
                  sectionScope: sectionScope?.normalized || null,
                  sectionSeedChunkIndex: seed.chunkIndex
                }
              });
            }
          }

          addRetrievalReason('linked_header_trailing_expansion');
          if (sectionScope) {
            addRetrievalReason('linked_section_trailing_expansion');
          }
        }

        candidates.push(...chunkCandidates);
      } else {
        candidates.push({
          source: 'linkedDocs',
          text: docName ? `${docName}: ${linkedText}` : linkedText,
          baseScore: 1.1,
          recencyBoost: 0,
          meta: {
            docId: linkedDocId,
            docName: docName || null,
            label: docName || `Doc ${linkedDocId}`,
            contextRole: isTemplateDoc ? 'style_reference' : 'supporting',
            trigger: isTemplateDoc ? 'linked_doc_template' : 'linked_doc_supporting'
          }
        });

        const anchoredSnippets = extractKeywordAnchoredSnippets({
          text: linkedLongText,
          userText,
          maxSnippets: 3,
          windowChars: 360
        });
        anchoredSnippets.forEach((row, index) => {
          candidates.push({
            source: 'linkedDocs',
            text: docName ? `${docName}: ${row.snippet}` : row.snippet,
            baseScore: 1.38 - (0.06 * index),
            recencyBoost: 0,
            meta: {
              docId: linkedDocId,
              docName: docName || null,
              label: docName || `Doc ${linkedDocId}`,
              contextRole: 'linked_keyword_anchor',
              matchedKeyword: row.keyword || null,
              trigger: 'linked_keyword_anchor'
            }
          });
        });
      }
    }
  }

  const attachmentSufficiency = evaluateAttachmentSufficiency({
    items: candidates,
    includeActiveDoc: !isActiveChatDoc,
    minItems: Number(CONFIG.AI?.CHAT_ORCHESTRATION?.ATTACHMENT_SUFFICIENCY_MIN_ITEMS || DEFAULT_ATTACHMENT_SUFFICIENCY_MIN_ITEMS),
    minChars: Number(CONFIG.AI?.CHAT_ORCHESTRATION?.ATTACHMENT_SUFFICIENCY_MIN_CHARS || DEFAULT_ATTACHMENT_SUFFICIENCY_MIN_CHARS)
  });
  const attachmentItems = (Array.isArray(candidates) ? candidates : []).filter((item) => {
    const source = String(item?.source || '').trim();
    if (source === 'linkedDocs') return true;
    return !isActiveChatDoc && source === 'activeDoc';
  });
  const attachmentKeywordCoverageRaw = buildContextKeywordCoverage({ userText, items: attachmentItems });
  const attachmentDefaultTargetAssumed = attachmentItems.length > 0 && promptTargetSignals.shouldAssumeAttachmentTarget;
  const attachmentKeywordCoverage = attachmentDefaultTargetAssumed
    ? {
      ...attachmentKeywordCoverageRaw,
      userKeywords: [],
      matchedKeywords: [],
      missingKeywords: [],
      coverageRatio: 1,
      implicitTarget: 'attachment_default'
    }
    : attachmentKeywordCoverageRaw;
  const attachmentKeywordCoverageSufficient = attachmentDefaultTargetAssumed
    || Number(attachmentKeywordCoverage?.coverageRatio || 0) >= ATTACHMENT_KEYWORD_COVERAGE_MIN;
  const shouldRunProjectSearch = Boolean(plan.includeProjectSearch)
    && projectId > 0
    && typeof searchProject === 'function'
    && (!attachmentSufficiency.isSufficient || !attachmentKeywordCoverageSufficient);

  if (Boolean(plan.includeProjectSearch)) {
    if (attachmentSufficiency.isSufficient && attachmentKeywordCoverageSufficient) {
      addRetrievalReason('attachment_context_sufficient');
      if (attachmentDefaultTargetAssumed) {
        addRetrievalReason('attachment_default_target_assumed');
      }
    } else {
      addRetrievalReason('insufficient_attachment_context');
      if (!attachmentKeywordCoverageSufficient) {
        addRetrievalReason('attachment_keyword_coverage_low');
      }
    }
  }

  if (shouldRunProjectSearch) {
    addRetrievalReason('project_retrieval_triggered');
    const queries = Array.isArray(queryPlan?.queries)
      ? queryPlan.queries.map(value => String(value || '').trim()).filter(Boolean)
      : [];
    const fallbackQuery = String(userText || '').trim();
    const maxProjectQueries = Math.max(1, Math.min(4, Number(queryPlan?.queryExpansion?.maxQueries || 3)));
    const plannedQueries = Array.from(new Set([
      ...queries,
      fallbackQuery
    ].filter(Boolean))).slice(0, maxProjectQueries);
    plannedProjectQueries = plannedQueries;

    if (plannedQueries.length > 1) {
      addRetrievalReason('project_retrieval_multi_query');
    }

    for (const projectQuery of plannedQueries) {
      if (!projectQuery) continue;
      attemptedProjectQueries.push(projectQuery);
      try {
        const projectQueryKeywords = tokenizeForAudit(projectQuery).slice(0, 10);
        const scoringKeywords = Array.from(new Set([
          ...projectQueryKeywords,
          ...intentContractPrimaryKeywords
        ])).slice(0, 14);
        const projectAnchorTokens = projectQueryKeywords.filter((token) => isAuditKeywordToken(token, { minLength: 4 }));
        const normalizedProjectQuery = normalizePromptComparableText(projectQuery);
        const projectNameExactFloor = Math.max(
          0,
          Number(CONFIG.AI?.CHAT_ORCHESTRATION?.PROJECT_NAME_EXACT_SCORE_FLOOR || 18)
        );
        const projectNameExactMultiplier = Math.max(
          1,
          Number(CONFIG.AI?.CHAT_ORCHESTRATION?.PROJECT_NAME_EXACT_MULTIPLIER || 2.2)
        );
        const projectNamePrefixMultiplier = Math.max(
          1,
          Number(CONFIG.AI?.CHAT_ORCHESTRATION?.PROJECT_NAME_PREFIX_MULTIPLIER || 1.25)
        );
        const keywordExactUniqueWeight = Math.max(
          0,
          Number(CONFIG.AI?.CHAT_ORCHESTRATION?.PROJECT_KEYWORD_EXACT_UNIQUE_WEIGHT || 0.09)
        );
        const keywordExactTotalWeight = Math.max(
          0,
          Number(CONFIG.AI?.CHAT_ORCHESTRATION?.PROJECT_KEYWORD_EXACT_TOTAL_WEIGHT || 0.03)
        );
        const keywordFuzzyUniqueWeight = Math.max(
          0,
          Number(CONFIG.AI?.CHAT_ORCHESTRATION?.PROJECT_KEYWORD_FUZZY_UNIQUE_WEIGHT || 0.03)
        );
        const keywordFuzzyTotalWeight = Math.max(
          0,
          Number(CONFIG.AI?.CHAT_ORCHESTRATION?.PROJECT_KEYWORD_FUZZY_TOTAL_WEIGHT || 0.01)
        );
        const keywordExactTotalCap = clampPositive(
          CONFIG.AI?.CHAT_ORCHESTRATION?.PROJECT_KEYWORD_EXACT_TOTAL_CAP,
          10
        );
        const keywordFuzzyTotalCap = clampPositive(
          CONFIG.AI?.CHAT_ORCHESTRATION?.PROJECT_KEYWORD_FUZZY_TOTAL_CAP,
          8
        );
        const keywordSignalMultiplierCap = Math.max(
          1,
          Number(CONFIG.AI?.CHAT_ORCHESTRATION?.PROJECT_KEYWORD_SIGNAL_MULTIPLIER_CAP || 1.55)
        );
        const multiKeywordBoostPerExtra = Math.max(
          0,
          Number(CONFIG.AI?.CHAT_ORCHESTRATION?.PROJECT_KEYWORD_MULTI_MATCH_BOOST_PER_EXTRA || 0.3)
        );
        const multiKeywordBoostCap = Math.max(
          1,
          Number(CONFIG.AI?.CHAT_ORCHESTRATION?.PROJECT_KEYWORD_MULTI_MATCH_BOOST_CAP || 2.2)
        );
        let projectAnchorPenaltyRows = 0;
        let projectAnchorBoostedRows = 0;
        const countKeywordHits = (value = '') => {
          const lowered = String(value || '').toLowerCase();
          if (!lowered || scoringKeywords.length === 0) {
            return { unique: 0, total: 0 };
          }
          let unique = 0;
          let total = 0;
          for (const token of scoringKeywords) {
            const needle = String(token || '').trim().toLowerCase();
            if (!needle) continue;
            let index = lowered.indexOf(needle);
            let tokenHits = 0;
            while (index >= 0 && tokenHits < 12) {
              total += 1;
              tokenHits += 1;
              index = lowered.indexOf(needle, index + needle.length);
            }
            if (tokenHits > 0) unique += 1;
          }
          return { unique, total };
        };
        const countKeywordSignalHits = (value = '') => {
          const lowered = String(value || '').toLowerCase();
          if (!lowered || scoringKeywords.length === 0) {
            return {
              exactUnique: 0,
              exactTotal: 0,
              fuzzyUnique: 0,
              fuzzyTotal: 0,
              matchedTokens: []
            };
          }

          const tokenItems = buildKeywordTokenItems(lowered);
          let exactUnique = 0;
          let exactTotal = 0;
          let fuzzyUnique = 0;
          let fuzzyTotal = 0;
          const matchedTokens = new Set();

          for (const token of scoringKeywords) {
            const needle = String(token || '').trim().toLowerCase();
            if (!needle) continue;

            const exactHits = countWholeWordTokenHits(lowered, needle);
            const hasExact = exactHits > 0;
            const hasFuzzy = !hasExact && fuzzyKeywordMatch({ keyword: needle, tokenItems });

            if (hasExact) {
              exactUnique += 1;
              exactTotal += Math.min(3, exactHits);
              matchedTokens.add(needle);
              continue;
            }

            if (hasFuzzy) {
              fuzzyUnique += 1;
              fuzzyTotal += 1;
              matchedTokens.add(needle);
            }
          }

          return {
            exactUnique,
            exactTotal: Math.min(keywordExactTotalCap, exactTotal),
            fuzzyUnique,
            fuzzyTotal: Math.min(keywordFuzzyTotalCap, fuzzyTotal),
            matchedTokens: Array.from(matchedTokens)
          };
        };
        const countAnchorHits = (value = '') => {
          const lowered = String(value || '').toLowerCase();
          if (!lowered || projectAnchorTokens.length === 0) {
            return {
              unique: 0,
              total: 0
            };
          }

          let unique = 0;
          let total = 0;
          projectAnchorTokens.forEach((token) => {
            const tokenHits = countWholeWordTokenHits(lowered, token);
            if (tokenHits > 0) {
              unique += 1;
              total += tokenHits;
            }
          });

          return {
            unique,
            total
          };
        };

        const effectiveProjectSearchLimit = isActiveChatDoc && projectChatNoiseEnabled
          ? Math.min(
            projectChatNoiseSearchLimitCap,
            Math.max(maxProjectResults, Math.floor(maxProjectResults * projectChatNoiseSearchLimitMultiplier))
          )
          : maxProjectResults;
        let payload = await searchProject(projectId, projectQuery, {
          profile: 'AI_CONTEXT',
          allowLimitOverride: true,
          maxLimit: projectSearchDeepLimitCap,
          excludeLikelyChatDocs: isActiveChatDoc,
          returnContent: false,
          resultType: 'chunks',
          frontendFiltering: false,
          limit: effectiveProjectSearchLimit,
          signal
        });

        let rows = Array.isArray(payload?.results) ? payload.results : [];
        if (isActiveChatDoc && projectSearchExcludeChatDocsWhenActiveChat) {
          let attempts = 0;
          let searchLimit = effectiveProjectSearchLimit;
          let nonChatRows = rows.filter((row) => !isLikelyChatSearchResult(row));
          let previousRowCount = rows.length;
          let previousNonChatCount = nonChatRows.length;

          while (nonChatRows.length < projectSearchMinNonChatRows && searchLimit < projectSearchDeepLimitCap && attempts < 3) {
            attempts += 1;
            searchLimit = Math.min(projectSearchDeepLimitCap, Math.max(searchLimit + 1, Math.floor(searchLimit * 2)));
            payload = await searchProject(projectId, projectQuery, {
              profile: 'AI_CONTEXT',
              allowLimitOverride: true,
              maxLimit: projectSearchDeepLimitCap,
              excludeLikelyChatDocs: isActiveChatDoc,
              returnContent: false,
              resultType: 'chunks',
              frontendFiltering: false,
              limit: searchLimit,
              signal
            });
            rows = Array.isArray(payload?.results) ? payload.results : [];
            nonChatRows = rows.filter((row) => !isLikelyChatSearchResult(row));

            const didNotGrowRows = rows.length <= previousRowCount;
            const didNotGrowNonChatRows = nonChatRows.length <= previousNonChatCount;
            if (didNotGrowRows && didNotGrowNonChatRows) {
              addRetrievalReason('project_search_deepening_no_progress');
              break;
            }
            previousRowCount = rows.length;
            previousNonChatCount = nonChatRows.length;
          }

          if (attempts > 0) {
            addRetrievalReason('project_search_limit_expanded_for_non_chat');
          }
        }

        const hasNonChatRows = rows.some((row) => !isLikelyChatSearchResult(row));
        const hasAttachmentDocCandidates = candidates.some((candidate) => {
          const source = String(candidate?.source || '').trim();
          return source === 'activeDoc' || source === 'linkedDocs';
        });
        const shouldPreferDocsOverChatRows = hasNonChatRows || hasAttachmentDocCandidates;

        const projectChunkHashes = Array.from(new Set(
          rows.flatMap((row) => {
            const snippetRows = Array.isArray(row?.content_snippets) ? row.content_snippets : [];
            const fromSnippets = snippetRows
              .map((snippet) => String(snippet?.chunk_hash || '').trim())
              .filter(Boolean);
            const fallbackHash = String(row?.content_source_chunk_hash || row?.chunk_hash || '').trim();
            return fallbackHash ? [...fromSnippets, fallbackHash] : fromSnippets;
          })
        ));

        const fullChunkTextByHash = new Map();
        if (projectChunkHashes.length > 0) {
          const chunkRowsByHash = await apiLoadChunksByHashes(projectChunkHashes);
          (Array.isArray(chunkRowsByHash) ? chunkRowsByHash : []).forEach((chunkRow) => {
            const chunkHash = String(chunkRow?.chunk_hash || '').trim();
            if (!chunkHash) return;
            const markdown = normalizeContextText(chunkRow?.content_md || '');
            const fallbackText = normalizeContextText(nodeTextContent(chunkRow?.content_json || {}));
            const fullText = markdown || fallbackText;
            if (fullText) fullChunkTextByHash.set(chunkHash, fullText);
          });
        }

        const chatDocChunkCounts = new Map();
        let projectChatRowsKept = 0;
        for (const row of rows) {
          const rowDocId = Number(row?.doc_id || 0);
          const rowScore = toFiniteNumber(row?.chunk_score, toFiniteNumber(row?.total_score, 0));
          if (activeDocId > 0 && rowDocId === activeDocId) {
            excludedActiveChatDocSearchHits += 1;
            continue;
          }
          const rowIsLikelyChat = isLikelyChatSearchResult(row);
          if (isActiveChatDoc && projectSearchExcludeChatDocsWhenActiveChat && rowIsLikelyChat) {
            projectChatNoiseSuppressedRows += 1;
            continue;
          }
          if (projectChatNoiseEnabled && rowIsLikelyChat && shouldPreferDocsOverChatRows) {
            projectChatNoiseSuppressedRows += 1;
            continue;
          }

          let adjustedRowScore = rowScore;
          const rowDocName = String(row?.doc_name || '').trim();
          const normalizedRowDocName = normalizePromptComparableText(rowDocName);
          const contractDocHintMatch = intentContractDocHints.some((hint) => {
            if (!hint?.normalized || hint.normalized.length < 3) return false;
            return Boolean(normalizedRowDocName && (normalizedRowDocName.includes(hint.normalized) || hint.normalized.includes(normalizedRowDocName)));
          });
          const docNameExactHit = Boolean(normalizedProjectQuery && normalizedRowDocName && normalizedProjectQuery === normalizedRowDocName);
          const docNamePrefixHit = Boolean(
            normalizedProjectQuery
            && normalizedRowDocName
            && !docNameExactHit
            && (normalizedRowDocName.startsWith(normalizedProjectQuery) || normalizedProjectQuery.startsWith(normalizedRowDocName))
          );
          if (projectChatNoiseEnabled && rowIsLikelyChat) {
            if (projectChatRowsKept >= projectChatMaxChunks) {
              projectChatNoiseCappedRows += 1;
              continue;
            }
            projectChatRowsKept += 1;
            if (rowDocId > 0) {
              const nextCount = Number(chatDocChunkCounts.get(rowDocId) || 0) + 1;
              chatDocChunkCounts.set(rowDocId, nextCount);
              if (nextCount > projectChatNoisePerDocMaxChunks) {
                projectChatNoiseCappedRows += 1;
                continue;
              }
            }
            const fallbackMultiplier = shouldPreferDocsOverChatRows
              ? projectChatNoiseDeprioritizeMultiplier
              : projectChatFallbackMultiplier;
            adjustedRowScore = Math.max(0, rowScore * fallbackMultiplier);
            if (!shouldPreferDocsOverChatRows) {
              projectChatFallbackRows += 1;
            }
            if (adjustedRowScore < rowScore) {
              projectChatNoiseDownscoredRows += 1;
            }
          } else if (!rowIsLikelyChat && (isActiveChatDoc || exhaustiveScopeRequested)) {
            adjustedRowScore = adjustedRowScore * projectNonChatPreferenceMultiplier;
          }

          if (!rowIsLikelyChat && docNameExactHit) {
            adjustedRowScore = Math.max(projectNameExactFloor, adjustedRowScore * projectNameExactMultiplier);
            addRetrievalReason('project_doc_name_exact_guardrail');
          } else if (!rowIsLikelyChat && docNamePrefixHit) {
            adjustedRowScore = adjustedRowScore * projectNamePrefixMultiplier;
            addRetrievalReason('project_doc_name_prefix_guardrail');
          }
          if (!rowIsLikelyChat && contractDocHintMatch) {
            adjustedRowScore = adjustedRowScore * intentContractDocHintMultiplier;
            addRetrievalReason('project_doc_name_contract_hint');
          }

          const chunkRows = Array.isArray(row?.content_snippets)
            ? row.content_snippets
            : [];

          // eslint-disable-next-line no-loop-func
          const getDiversityAdjustedScore = (score) => {
            if (!shouldApplyProjectDocDiversity || rowDocId <= 0) {
              return {
                skip: false,
                adjustedScore: score,
                rankInDoc: 0
              };
            }

            const seenCount = Number(projectDocCandidateCounts.get(rowDocId) || 0);
            if (seenCount >= projectSearchPerDocMaxChunks) {
              projectDocDiversityCappedRows += 1;
              return {
                skip: true,
                adjustedScore: score,
                rankInDoc: seenCount
              };
            }

            projectDocCandidateCounts.set(rowDocId, seenCount + 1);
            const adjustedScore = Math.max(0, Number(score || 0) - (projectSearchPerDocScoreDecay * seenCount));
            if (adjustedScore < Number(score || 0) && seenCount > 0) {
              projectDocDiversityDownscoredRows += 1;
            }

            return {
              skip: false,
              adjustedScore,
              rankInDoc: seenCount
            };
          };

          if (chunkRows.length === 0) {
            const resolvedSnippet = resolveSnippetWithMarkerIntegrity({
              marked: row?.content_marked || '',
              plain: row?.content_snippet || ''
            });
            const fallbackHash = String(row?.content_source_chunk_hash || row?.chunk_hash || '').trim();
            const fullChunkText = fallbackHash ? String(fullChunkTextByHash.get(fallbackHash) || '').trim() : '';
            const singleText = fullChunkText || resolvedSnippet.text;
            if (!singleText) continue;
            const diversity = getDiversityAdjustedScore(adjustedRowScore);
            if (diversity.skip) continue;
            const keywordHits = countKeywordHits(singleText);
            const keywordSignalHits = countKeywordSignalHits(singleText);
            const keywordSignalBoost = Math.min(
              keywordSignalMultiplierCap,
              1
                + (keywordSignalHits.exactUnique * keywordExactUniqueWeight)
                + (keywordSignalHits.exactTotal * keywordExactTotalWeight)
                + (keywordSignalHits.fuzzyUnique * keywordFuzzyUniqueWeight)
                + (keywordSignalHits.fuzzyTotal * keywordFuzzyTotalWeight)
            );
            const matchedKeywordCount = Array.isArray(keywordSignalHits.matchedTokens)
              ? keywordSignalHits.matchedTokens.length
              : 0;
            const multiKeywordBoost = Math.min(
              multiKeywordBoostCap,
              1 + (Math.max(0, matchedKeywordCount - 1) * multiKeywordBoostPerExtra)
            );
            const anchorHits = countAnchorHits(singleText);
            const hasAnchors = projectAnchorTokens.length > 0;
            const anchorCoverage = hasAnchors ? (anchorHits.unique / projectAnchorTokens.length) : 0;
            let anchorMultiplier = 1;
            if (hasAnchors) {
              if (anchorHits.unique === 0) {
                anchorMultiplier *= 0.22;
                projectAnchorPenaltyRows += 1;
              } else if (anchorCoverage >= 1) {
                anchorMultiplier *= 2.35;
                projectAnchorBoostedRows += 1;
              } else if (anchorCoverage >= 0.5) {
                anchorMultiplier *= 1.6;
                projectAnchorBoostedRows += 1;
              }
            }
            if (resolvedSnippet.usedMarkerFallback) {
              addRetrievalReason('marker_invalid_fallback_plain');
            }
            const docName = String(row?.doc_name || '').trim();
            candidates.push({
              source: 'projectSearch',
              text: docName ? `${docName}: ${singleText}` : singleText,
              baseScore: diversity.adjustedScore * keywordSignalBoost * multiKeywordBoost * anchorMultiplier,
              recencyBoost: 0,
              meta: {
                docId: row?.doc_id ?? null,
                chunkHash: row?.content_source_chunk_hash || row?.chunk_hash || null,
                docRankInSelection: diversity.rankInDoc,
                keywordHitUniqueCount: keywordHits.unique,
                keywordHitTotalCount: keywordHits.total,
                keywordExactUniqueCount: keywordSignalHits.exactUnique,
                keywordExactTotalCount: keywordSignalHits.exactTotal,
                keywordFuzzyUniqueCount: keywordSignalHits.fuzzyUnique,
                keywordFuzzyTotalCount: keywordSignalHits.fuzzyTotal,
                matchedKeywordCount,
                matchedKeywords: keywordSignalHits.matchedTokens,
                anchorHitUniqueCount: anchorHits.unique,
                anchorHitTotalCount: anchorHits.total,
                trigger: 'project_search_primary_chunk',
                query: projectQuery
              }
            });
            const sourceChunkHash = String(row?.content_source_chunk_hash || row?.chunk_hash || '').trim();
            if (sourceChunkHash) fetchedChunkIds.add(sourceChunkHash);
            const signalDocId = Number(row?.doc_id || 0);
            if (signalDocId > 0) {
              const existingSignal = projectDocSignals.get(signalDocId);
              const signalScore = adjustedRowScore;
              if (!existingSignal || signalScore > existingSignal.score) {
                projectDocSignals.set(signalDocId, {
                  docId: signalDocId,
                  docName: docName || null,
                  score: signalScore
                });
              }
            }
            continue;
          }

          const shouldExpandAdjacentChunks = adjacentChunkExpansionEnabled && adjustedRowScore >= adjacentChunkMinSourceScore;
          const chunksToUse = shouldExpandAdjacentChunks
            ? chunkRows.slice(0, adjacentChunkMaxPerDoc)
            : chunkRows.slice(0, 1);
          if (adjacentChunkExpansionEnabled && chunksToUse.length > 1) {
            addRetrievalReason('adjacent_chunk_expanded');
          }

          for (let chunkIndex = 0; chunkIndex < chunksToUse.length; chunkIndex += 1) {
            const chunk = chunksToUse[chunkIndex];
            const hotChunkHash = String(chunksToUse?.[0]?.chunk_hash || '').trim() || null;
            const chunkHash = String(chunk?.chunk_hash || '').trim();
            if (chunkHash) fetchedChunkIds.add(chunkHash);
            const resolvedSnippet = resolveSnippetWithMarkerIntegrity({
              marked: chunk?.content_marked || '',
              plain: chunk?.content_snippet || ''
            });
            const fullChunkText = chunkHash ? String(fullChunkTextByHash.get(chunkHash) || '').trim() : '';
            const snippet = fullChunkText || resolvedSnippet.text;
            if (!snippet) continue;
            const diversity = getDiversityAdjustedScore(adjustedRowScore);
            if (diversity.skip) break;
            const keywordHits = countKeywordHits(snippet);
            const keywordSignalHits = countKeywordSignalHits(snippet);
            const keywordSignalBoost = Math.min(
              keywordSignalMultiplierCap,
              1
                + (keywordSignalHits.exactUnique * keywordExactUniqueWeight)
                + (keywordSignalHits.exactTotal * keywordExactTotalWeight)
                + (keywordSignalHits.fuzzyUnique * keywordFuzzyUniqueWeight)
                + (keywordSignalHits.fuzzyTotal * keywordFuzzyTotalWeight)
            );
            const matchedKeywordCount = Array.isArray(keywordSignalHits.matchedTokens)
              ? keywordSignalHits.matchedTokens.length
              : 0;
            const multiKeywordBoost = Math.min(
              multiKeywordBoostCap,
              1 + (Math.max(0, matchedKeywordCount - 1) * multiKeywordBoostPerExtra)
            );
            const anchorHits = countAnchorHits(snippet);
            const hasAnchors = projectAnchorTokens.length > 0;
            const anchorCoverage = hasAnchors ? (anchorHits.unique / projectAnchorTokens.length) : 0;
            let anchorMultiplier = 1;
            if (hasAnchors) {
              if (anchorHits.unique === 0) {
                anchorMultiplier *= 0.22;
                projectAnchorPenaltyRows += 1;
              } else if (anchorCoverage >= 1) {
                anchorMultiplier *= 2.35;
                projectAnchorBoostedRows += 1;
              } else if (anchorCoverage >= 0.5) {
                anchorMultiplier *= 1.6;
                projectAnchorBoostedRows += 1;
              }
            }
            if (resolvedSnippet.usedMarkerFallback) {
              addRetrievalReason('marker_invalid_fallback_plain');
            }
            const docName = String(row?.doc_name || '').trim();
            const baseChunkScore = diversity.adjustedScore;
            const adjustedChunkScore = Math.max(
              0,
              (baseChunkScore - (adjacentChunkScoreDecay * chunkIndex))
                * keywordSignalBoost
                * multiKeywordBoost
                * anchorMultiplier
            );
            if (chunkIndex > 0 && adjustedChunkScore < adjacentChunkContinueMinScore) {
              break;
            }
            candidates.push({
              source: 'projectSearch',
              text: docName ? `${docName}: ${snippet}` : snippet,
              baseScore: adjustedChunkScore,
              recencyBoost: 0,
              meta: {
                docId: row?.doc_id ?? null,
                chunkHash: chunk?.chunk_hash || null,
                chunkRankInDoc: chunkIndex,
                docRankInSelection: diversity.rankInDoc,
                keywordHitUniqueCount: keywordHits.unique,
                keywordHitTotalCount: keywordHits.total,
                keywordExactUniqueCount: keywordSignalHits.exactUnique,
                keywordExactTotalCount: keywordSignalHits.exactTotal,
                keywordFuzzyUniqueCount: keywordSignalHits.fuzzyUnique,
                keywordFuzzyTotalCount: keywordSignalHits.fuzzyTotal,
                matchedKeywordCount,
                matchedKeywords: keywordSignalHits.matchedTokens,
                anchorHitUniqueCount: anchorHits.unique,
                anchorHitTotalCount: anchorHits.total,
                piggybackFromChunkId: chunkIndex > 0 ? hotChunkHash : null,
                piggybackReason: chunkIndex > 0 ? 'adjacent_to_hot_hit' : null,
                trigger: chunkIndex === 0 ? 'project_search_chunk_hit' : 'project_search_adjacent_chunk',
                query: projectQuery
              }
            });
          }

          const signalDocId = Number(row?.doc_id || 0);
          if (signalDocId > 0) {
            const existingSignal = projectDocSignals.get(signalDocId);
            const signalScore = adjustedRowScore;
            if (!existingSignal || signalScore > existingSignal.score) {
              projectDocSignals.set(signalDocId, {
                docId: signalDocId,
                docName: String(row?.doc_name || '').trim() || null,
                score: signalScore
              });
            }
          }
        }

        if (projectAnchorPenaltyRows > 0) {
          addRetrievalReason('project_keyword_anchor_penalty');
        }
        if (projectAnchorBoostedRows > 0) {
          addRetrievalReason('project_keyword_anchor_boost');
        }
      } catch (error) {
        projectSearchErrors.push({
          query: projectQuery,
          message: String(error?.message || 'project search retrieval failed')
        });
        debug('buildContextRetrievalPipeline', { type: 'warn' }, error?.message || 'project search retrieval failed');
      }
    }
  }

  const canEscalateFullDoc = shouldRunProjectSearch
    && fullDocEscalationEnabled
    && Boolean(queryPlan?.refine?.applied)
    && typeof loadLinkedDocContent === 'function'
    && projectDocSignals.size > 0;

  if (canEscalateFullDoc) {
    const projectSearchCandidates = candidates.filter((item) => String(item?.source || '').trim() === 'projectSearch');
    const coverageBefore = buildContextKeywordCoverage({ userText, items: projectSearchCandidates });
    fullDocEscalationCoverageBefore = coverageBefore;
    if (Number(coverageBefore?.coverageRatio || 0) < fullDocEscalationMinCoverageRatio) {
      fullDocEscalationTriggered = true;
      const docsToEscalate = Array.from(projectDocSignals.values())
        .sort((left, right) => Number(right?.score || 0) - Number(left?.score || 0))
        .slice(0, fullDocEscalationMaxDocs);

      for (const docSignal of docsToEscalate) {
        const docId = Number(docSignal?.docId || 0);
        if (docId <= 0) continue;
        try {
          const payload = await loadLinkedDocContent(docId);
          const docJsonValue = extractDocJsonFromLoadedDoc(payload);
          const fallbackText = normalizeContextText(payload?.content || '');
          const fullDocText = docJsonValue
            ? extractActiveDocContextText({
              docJson: docJsonValue,
              maxChars: Math.min(maxContextChars, fullDocEscalationMaxCharsPerDoc)
            })
            : (fallbackText ? fallbackText.slice(0, fullDocEscalationMaxCharsPerDoc) : '');
          if (!fullDocText) continue;

          const docName = String(payload?.name || docSignal?.docName || '').trim();
          candidates.push({
            source: 'projectSearch',
            text: docName ? `${docName}: ${fullDocText}` : fullDocText,
            baseScore: Math.max(0.6, Number(docSignal?.score || 0) * 0.72),
            recencyBoost: 0,
            meta: {
              docId,
              docName: docName || null,
              contextRole: 'full_doc_escalation',
              fullDocEscalation: true,
              chunkHash: null,
              trigger: 'project_search_full_doc_escalation'
            }
          });
          fullDocEscalationDocIds.push(docId);
        } catch (error) {
          fullDocEscalationErrors.push({
            docId,
            message: String(error?.message || 'full-doc escalation failed')
          });
        }
      }

      if (fullDocEscalationDocIds.length > 0) {
        addRetrievalReason('project_full_doc_escalated');
      }

      const coverageAfter = buildContextKeywordCoverage({
        userText,
        items: candidates.filter((item) => String(item?.source || '').trim() === 'projectSearch')
      });
      fullDocEscalationCoverageAfter = coverageAfter;
    }
  }

  if (hasTemplateDoc) {
    let guaranteedTemplateChunk = false;
    for (const candidate of candidates) {
      if (String(candidate?.source || '').trim() !== 'linkedDocs') continue;
      if (Number(candidate?.meta?.docId || 0) !== templateDocId) continue;
      if (Number(candidate?.meta?.chunkIndex) !== 0) continue;

      candidate.baseScore = Math.max(
        toFiniteNumber(candidate.baseScore, 0),
        TEMPLATE_FIRST_CHUNK_GUARANTEE_SCORE
      );
      guaranteedTemplateChunk = true;
    }

    if (guaranteedTemplateChunk) {
      addRetrievalReason('template-first-chunk-guaranteed');
    }
  }

  const routedCandidates = applyRouterGuidedAdjustments({
    candidates,
    routerContract,
    addRetrievalReason
  });

  let keywordReservedItems = [];
  if (shouldRunProjectSearch
    && CONFIG.AI?.CHAT_ORCHESTRATION?.PROJECT_KEYWORD_DIVERSITY_RESERVE_ENABLED !== false) {
    const reservePerKeyword = Math.max(
      1,
      Number(CONFIG.AI?.CHAT_ORCHESTRATION?.PROJECT_KEYWORD_DIVERSITY_RESERVE_PER_KEYWORD || 1)
    );
    const maxReserveKeywords = Math.max(
      1,
      Number(CONFIG.AI?.CHAT_ORCHESTRATION?.PROJECT_KEYWORD_DIVERSITY_MAX_KEYWORDS || 4)
    );
    const dynamicReserveEnabled =
      CONFIG.AI?.CHAT_ORCHESTRATION?.PROJECT_KEYWORD_DIVERSITY_DYNAMIC_RESERVE_ENABLED !== false;
    const dynamicTopScoreRatio = Math.max(
      0,
      Math.min(1, Number(CONFIG.AI?.CHAT_ORCHESTRATION?.PROJECT_KEYWORD_DIVERSITY_DYNAMIC_TOP_SCORE_RATIO || 0.78))
    );
    const dynamicMaxPerKeyword = Math.max(
      reservePerKeyword,
      Number(CONFIG.AI?.CHAT_ORCHESTRATION?.PROJECT_KEYWORD_DIVERSITY_DYNAMIC_MAX_PER_KEYWORD || 4)
    );
    const dynamicMinScore = Math.max(
      0,
      Number(CONFIG.AI?.CHAT_ORCHESTRATION?.PROJECT_KEYWORD_DIVERSITY_DYNAMIC_MIN_SCORE || 0.8)
    );
    const reserveKeywords = Array.from(new Set([
      ...tokenizeForAudit(userText),
      ...intentContractPrimaryKeywords
    ]))
      .filter((token) => isAuditKeywordToken(token, { minLength: 4 }))
      .slice(0, maxReserveKeywords);

    if (reserveKeywords.length > 0) {
      const scoredProjectCandidates = routedCandidates
        .filter((item) => String(item?.source || '').trim() === 'projectSearch')
        .map((item) => ({
          item,
          score: toFiniteNumber(item?.score, toFiniteNumber(item?.baseScore, 0)),
          matchedKeywords: Array.isArray(item?.meta?.matchedKeywords)
            ? item.meta.matchedKeywords.map((token) => String(token || '').trim().toLowerCase()).filter(Boolean)
            : []
        }));

      const reservedKeys = new Set();
      for (const keyword of reserveKeywords) {
        const normalizedKeyword = String(keyword || '').trim().toLowerCase();
        if (!normalizedKeyword) continue;
        const keywordMatches = scoredProjectCandidates
          .filter((row) => row.matchedKeywords.includes(normalizedKeyword))
          .sort((left, right) => right.score - left.score);

        if (keywordMatches.length === 0) continue;
        const topKeywordScore = Math.max(0, Number(keywordMatches[0]?.score || 0));
        const dynamicScoreFloor = Math.max(dynamicMinScore, topKeywordScore * dynamicTopScoreRatio);
        const dynamicCount = dynamicReserveEnabled
          ? keywordMatches.filter((row) => Number(row?.score || 0) >= dynamicScoreFloor).length
          : 0;
        const reserveCount = Math.max(
          reservePerKeyword,
          Math.min(dynamicMaxPerKeyword, dynamicCount)
        );

        keywordMatches.slice(0, reserveCount).forEach((row) => {
          const key = `${String(row.item?.source || '')}::${String(row.item?.text || '').trim().toLowerCase()}`;
          if (reservedKeys.has(key)) return;
          reservedKeys.add(key);
          keywordReservedItems.push({
            ...row.item,
            meta: {
              ...(row.item?.meta || {}),
              keywordReserveHit: normalizedKeyword,
              keywordReserveScoreFloor: dynamicScoreFloor,
              keywordReserveScore: Number(row?.score || 0)
            }
          });
        });
      }

      if (keywordReservedItems.length > 0) {
        addRetrievalReason('project_keyword_diversity_reserved');
      }
    }
  }

  const rankedInputCandidates = keywordReservedItems.length > 0
    ? routedCandidates.filter((item) => {
      const key = `${String(item?.source || '')}::${String(item?.text || '').trim().toLowerCase()}`;
      return !keywordReservedItems.some((reserved) => {
        const reservedKey = `${String(reserved?.source || '')}::${String(reserved?.text || '').trim().toLowerCase()}`;
        return reservedKey === key;
      });
    })
    : routedCandidates;

  const guaranteedActiveChatTailItems = [];
  if (isActiveChatDoc && plan.includeActiveDoc) {
    const tailPairs = extractRecentChatQaPairs({
      docJson,
      lastQaPairs: Math.max(1, activeChatTailMaxChunks)
    }).slice(-activeChatTailMaxChunks);

    tailPairs.forEach((pair, index) => {
      const userPart = normalizeContextText(pair?.user?.content || '');
      const assistantPart = normalizeContextText(pair?.assistant?.content || '');
      const text = [
        userPart ? `Recent user: ${userPart}` : '',
        assistantPart ? `Recent assistant: ${assistantPart}` : ''
      ].filter(Boolean).join('\n');
      if (!text) return;
      guaranteedActiveChatTailItems.push({
        source: 'activeDoc',
        text,
        baseScore: 0.03,
        recencyBoost: 0,
        recencyRank: index,
        sourcePriority: 999,
        score: 0.03,
        truncated: false,
        meta: {
          activeDocId: activeDocId || null,
          trigger: 'active_chat_tail_guaranteed',
          chunkRankInDoc: index
        }
      });
    });

    if (guaranteedActiveChatTailItems.length > 0) {
      addRetrievalReason('active_chat_tail_guaranteed');
    }
  }

  const reservedTailChars = guaranteedActiveChatTailItems.reduce((total, item) => {
    return total + Math.min(String(item?.text || '').trim().length, 480);
  }, 0);
  const reservedTailItems = guaranteedActiveChatTailItems.length;
  const reservedKeywordChars = keywordReservedItems.reduce((total, item) => {
    return total + Math.min(String(item?.text || '').trim().length, 900);
  }, 0);
  const reservedKeywordItems = keywordReservedItems.length;

  const ranked = rankAndBudgetContextItems({
    items: rankedInputCandidates,
    sourcePriorities: effectiveSourcePriorities,
    maxContextItems: Math.max(1, maxContextItems - reservedTailItems - reservedKeywordItems),
    maxContextChars: Math.max(512, maxContextChars - reservedTailChars - reservedKeywordChars),
    recencyWeight,
    recencyDecayTurns
  });

  let finalItems = Array.isArray(ranked.items) ? ranked.items.slice() : [];
  if (keywordReservedItems.length > 0) {
    const dedupeKeys = new Set(finalItems.map((item) => `${String(item?.source || '').trim()}::${String(item?.text || '').trim().toLowerCase()}`));
    keywordReservedItems.forEach((item) => {
      const key = `${String(item?.source || '').trim()}::${String(item?.text || '').trim().toLowerCase()}`;
      if (dedupeKeys.has(key)) return;
      dedupeKeys.add(key);
      finalItems.push(item);
    });
  }
  if (guaranteedActiveChatTailItems.length > 0) {
    const dedupeKeys = new Set(finalItems.map((item) => `${String(item?.source || '').trim()}::${String(item?.text || '').trim().toLowerCase()}`));
    guaranteedActiveChatTailItems.forEach((item) => {
      const key = `${String(item?.source || '').trim()}::${String(item?.text || '').trim().toLowerCase()}`;
      if (dedupeKeys.has(key)) return;
      dedupeKeys.add(key);
      finalItems.push(item);
    });
  }

  const finalSelectedChars = finalItems.reduce((total, item) => total + String(item?.text || '').trim().length, 0);

  const sourceBreakdown = summarizeSourceBreakdown(routedCandidates);
  const selectedSourceBreakdown = summarizeSourceBreakdown(finalItems);
  const selectedDocBreakdown = summarizeDocSelectionBreakdown(finalItems);
  const summaryBackfillNeededChunkHashes = Array.from(new Set(
    (Array.isArray(finalItems) ? finalItems : [])
      .map((item) => {
        const chunkHash = String(item?.meta?.chunkHash || '').trim();
        if (!chunkHash) return null;
        const score = toFiniteNumber(item?.score, toFiniteNumber(item?.baseScore, 0));
        const charLength = String(item?.text || '').trim().length;
        if (score < summaryMinCandidateScore || charLength < summaryMinChunkChars) {
          return null;
        }
        return chunkHash;
      })
      .filter(Boolean)
  )).slice(0, summaryMaxBackfillPerPrompt);

  if (summaryBackfillNeededChunkHashes.length > 0) {
    addRetrievalReason('summary_refresh_needed');
  }

  if (ranked.meta.totalCandidates > ranked.meta.selectedCount) {
    addRetrievalReason('budget-truncation');
  }
  if (excludedActiveChatDocSearchHits > 0) {
    addRetrievalReason('excluded-active-chat-doc-project-search-hit');
  }
  if (projectChatNoiseSuppressedRows > 0) {
    addRetrievalReason('project_chat_noise_suppressed');
  }
  if (projectChatNoiseCappedRows > 0) {
    addRetrievalReason('project_chat_noise_per_doc_cap');
  }
  if (projectChatNoiseDownscoredRows > 0) {
    addRetrievalReason('project_chat_noise_deprioritized');
  }
  if (projectChatFallbackRows > 0) {
    addRetrievalReason('project_chat_fallback_only');
  }
  if (projectDocDiversityCappedRows > 0) {
    addRetrievalReason('project_doc_diversity_capped');
  }
  if (projectDocDiversityDownscoredRows > 0) {
    addRetrievalReason('project_doc_diversity_downscored');
  }
  if (ranked.meta.selectedCount === 0) {
    addRetrievalReason('no-context-selected');
  }

  return {
    source: 'phase-c-context-retrieval',
    items: finalItems,
    meta: {
      ...ranked.meta,
      selectedCount: finalItems.length,
      selectedChars: finalSelectedChars,
      projectId: projectId || null,
      includeActiveDoc: Boolean(plan.includeActiveDoc),
      includeLinkedDocs: Boolean(plan.includeLinkedDocs),
      includeChatHistory: Boolean(plan.includeChatHistory),
      includeProjectSearch: Boolean(plan.includeProjectSearch),
      projectSearchTriggered: shouldRunProjectSearch,
      projectSearchQueryCount: attemptedProjectQueries.length,
      projectSearchPlannedQueries: plannedProjectQueries,
      projectSearchAttemptedQueries: attemptedProjectQueries,
      projectSearchErrors,
      fetchedChunkIds: Array.from(fetchedChunkIds),
      fullDocEscalationTriggered,
      fullDocEscalationDocIds,
      fullDocEscalationCoverageBefore,
      fullDocEscalationCoverageAfter,
      fullDocEscalationErrors,
      projectChatNoiseSuppressedRows,
      projectChatNoiseDownscoredRows,
      projectChatNoiseCappedRows,
      projectChatFallbackRows,
      projectDocDiversityCappedRows,
      projectDocDiversityDownscoredRows,
      attachmentContextCount: attachmentSufficiency.attachmentCount,
      attachmentContextChars: attachmentSufficiency.attachmentChars,
      attachmentContextSufficient: attachmentSufficiency.isSufficient,
      attachmentKeywordCoverage,
      attachmentKeywordCoverageSufficient,
      attachmentDefaultTargetAssumed,
      attachmentSufficiencyMinItems: attachmentSufficiency.requiredItems,
      attachmentSufficiencyMinChars: attachmentSufficiency.requiredChars,
      linkedDocIds,
      linkedDocCount: linkedDocIds.length,
      templateDocId: hasTemplateDoc ? templateDocId : null,
      isSingleDocContext,
      excludedActiveChatDocSearchHits,
      sourceBreakdown,
      selectedSourceBreakdown,
      selectedDocBreakdown,
      summaryBackfillNeededChunkHashes,
      retrievalReasons
    }
  };
};

